import React from "react";
import { Models } from "@hex-insights/app-modules";
import {
	BooleanField,
	FileInput,
	FileUploadField,
	FileUploadFieldInputProps,
	FormType,
	NumberField,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { MediaAttachmentFormValues } from "../../FormUtilities";
import { AnnouncementSelectField } from "../Announcement";
import { CalendarEventSelectField } from "../CalendarEvent";
import { CourseAssessmentSelectField } from "../CourseAssessment";
import { CourseAssessmentSubmissionSelectField } from "../CourseAssessmentSubmission";
import { MessageSelectField } from "../Message";
import { PersonSelectField } from "../Person";
import { PostSelectField } from "../Post";
import { BaseFieldProps } from "../shared";
import styles from "./field.module.css";

/**
 * Generic props for fields of the MediaAttachment model.
 */
type FieldProps<K extends keyof MediaAttachmentFormValues.Base = keyof MediaAttachmentFormValues.Base> = BaseFieldProps<
	Pick<MediaAttachmentFormValues.Base, K>
>;

/**
 * Field component for the `displayOrder` field of the MediaAttachment model.
 */
export function DisplayOrder({ formState, ...props }: FieldProps<"displayOrder">) {
	return (
		<NumberField
			formState={formState}
			name="displayOrder"
			optional={FormType.isCreate(formState.formType)}
			validationUnit={1}
			min={0}
			{...props}
		/>
	);
}

/**
 * Field component for the `title` field of the MediaAttachment model.
 */
export function Title({ formState, ...props }: FieldProps<"title">) {
	return <TextField formState={formState} name="title" optional {...props} />;
}

/**
 * Field component for the `description` field of the MediaAttachment model.
 */
export function Description({ formState, ...props }: FieldProps<"description">) {
	return <TextField formState={formState} name="description" multiLine optional Input={DescriptionInput} {...props} />;
}

function DescriptionInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={2} {...props} />;
}

/**
 * Field component for the `isFile` field of the MediaAttachment model.
 */
export function IsFile({ formState, ...props }: FieldProps<"isFile">) {
	return <BooleanField formState={formState} name="isFile" {...props} />;
}

/**
 * Field component for the `file` field of the MediaAttachment model.
 */
export function File({
	formState,
	url,
	label = "Image or File",
	...props
}: FieldProps<"fileKey" | "fileContentType" | "title"> & { url?: string }) {
	Models.requireURLForUpdateForm(url, false, formState.formType, "File");

	const {
		formSetFunctions: { title: setTitle },
	} = formState;
	const onFileChange = React.useCallback(
		(value: File | null, isInitial: boolean) => {
			if (isInitial) {
				return;
			}
			if (value) {
				setTitle(value.name);
			}
		},
		[setTitle],
	);

	return (
		<FileUploadField
			formState={formState}
			keyFieldName="fileKey"
			contentTypeFieldName="fileContentType"
			label={label}
			url={url}
			Input={MediaAttachmentFileInput}
			onFileChange={onFileChange}
			{...props}
		/>
	);
}

function MediaAttachmentFileInput(props: FileUploadFieldInputProps) {
	return (
		<FileInput
			{...props}
			menuClassName={styles["file__menu"]}
			menuImageEditorButtonContainerClassName={styles["file_menu__image-editor-button-container"]}
			menuStatusClassName={styles["file__menu__status"]}
		/>
	);
}

/**
 * Field component for the `linkURL` field of the MediaAttachment model.
 */
export function LinkURL({ formState, ...props }: FieldProps<"linkURL">) {
	return <TextField formState={formState} name="linkURL" {...props} />;
}

export namespace Announcement {
	export type SelectProps = FieldProps<"announcementID"> &
		Omit<AnnouncementSelectField.SelectProps<MediaAttachmentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `announcement` edge of the MediaAttachment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <AnnouncementSelectField.Select formState={formState} name="announcementID" optional {...props} />;
	}
}

export namespace CalendarEvent {
	export type SelectProps = FieldProps<"calendarEventID"> &
		Omit<CalendarEventSelectField.SelectProps<MediaAttachmentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `calendarEvent` edge of the MediaAttachment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CalendarEventSelectField.Select formState={formState} name="calendarEventID" optional {...props} />;
	}
}

export namespace CourseAssessment {
	export type SelectProps = FieldProps<"messageID"> &
		Omit<CourseAssessmentSelectField.SelectProps<MediaAttachmentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `courseAssessment` edge of the MediaAttachment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CourseAssessmentSelectField.Select formState={formState} name="messageID" optional {...props} />;
	}
}

export namespace CourseAssessmentSubmission {
	export type SelectProps = FieldProps<"messageID"> &
		Omit<
			CourseAssessmentSubmissionSelectField.SelectProps<MediaAttachmentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `courseAssessmentSubmission` edge of the MediaAttachment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CourseAssessmentSubmissionSelectField.Select formState={formState} name="messageID" optional {...props} />;
	}
}

export namespace Message {
	export type SelectProps = FieldProps<"messageID"> &
		Omit<MessageSelectField.SelectProps<MediaAttachmentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `message` edge of the MediaAttachment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <MessageSelectField.Select formState={formState} name="messageID" optional {...props} />;
	}
}

export namespace Post {
	export type SelectProps = FieldProps<"postID"> &
		Omit<PostSelectField.SelectProps<MediaAttachmentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `post` edge of the MediaAttachment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PostSelectField.Select formState={formState} name="postID" optional {...props} />;
	}
}

export namespace TaggedPeople {
	export type SelectProps = FieldProps<"taggedPersonIDs"> &
		Omit<PersonSelectField.MultiSelectProps<MediaAttachmentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `taggedPeople` edge of the MediaAttachment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PersonSelectField.MultiSelect formState={formState} name="taggedPersonIDs" {...props} />;
	}
}
